"use client";

import { useExperienciaHospedePage } from "./hooks/use-experiencia-hospede-page";
import { useSeo } from "@/features/shared/lib/use-seo";
import {
  HeroSection,
  RecursosSection,
  VantagensSection,
  ParaQuemSection,
  //CardapioDigitalSection,
  PorQueContratarSection,
} from "./components";
import { GenericInfoSection } from "@/features/shared/components/generic-info-section";
import { INFO_SECTIONS } from "@/features/shared/content/info-sections";

// Provas Sociais importadas da Home e UI
import { NumerosSection } from "@/features/home/components/numeros-section";
import { NaMidiaSection } from "@/features/home/components/na-midia-section";
import { SoftwareProductsCarousel, TrustedLogosMarquee, WallOfLoveSection, DorParallaxSection } from "@/features/shared/components";
import { FAQAccordion } from "@/features/ui/components/faq-accordion";
import { LeadCaptureCTA } from "@/features/ui/components/lead-capture-cta";
import { useLeadCapture } from "@/features/shared/lib/lead-capture-context";
import { UltimasDoBlogSection } from "@/features/shared/components/ultimas-do-blog-section-lazy";
import { getProdutoIcone } from "@/features/shared/data/produtos-data";

import { Spinner } from "@/components/ui/spinner";
import { Empty, EmptyHeader, EmptyTitle, EmptyDescription, EmptyContent } from "@/components/ui/empty";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

import { artigosMidia, depoimentos, numeros, videosData } from "@/features/shared/data/social-proof-data";

const LEAD_TITLE = "Experiência do Hóspede";

function ExperienciaHospedePage() {
  useSeo({
    title: "Aplicativo de Hospedagem e Experiência do Hóspede | Foco",
    description:
      "Aplicativo de hospedagem com check-in digital, comunicação personalizada e concierge virtual do pré-check-in ao pós-estadia. Monitore a satisfação do hóspede.",
    path: "/aplicativo-de-hospedagem",
  });

  const { openLeadCapture } = useLeadCapture();
  const { data, isLoading, isError, refetch } = useExperienciaHospedePage();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner className="w-12 h-12" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Empty className="text-center">
          <EmptyHeader>
            <EmptyTitle>Erro ao carregar dados</EmptyTitle>
            <EmptyDescription>
              Não foi possível carregar o conteúdo da página. Tente novamente.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button onClick={() => refetch()} variant="outline" className="rounded-full px-6">
              <RefreshCw className="w-4 h-4 mr-2" />
              Tentar novamente
            </Button>
          </EmptyContent>
        </Empty>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {/* Section 1: Hero */}
      <HeroSection
        data={data.hero}
        onCtaClick={() => openLeadCapture({ source: "form-aplicativo-de-hospedagem", title: LEAD_TITLE, icon: getProdutoIcone("/aplicativo-de-hospedagem") })}
      />

      {/* Section 2: O que é um motor de reservas */}
      <GenericInfoSection {...INFO_SECTIONS.experienciaHospede} imageSide="right" imageRounded imageBelowTitleOnMobile />

      {/* Diagnóstico: experiência ruim do hóspede (mesmo cenário da home) */}
      <DorParallaxSection
        dorId="experiencia-ruim"
        backgroundImage="/assets/imgs/experiencia-do-hospede/dor.webp"
        backgroundAlt="Família insatisfeita chegando ao hotel com malas, recepcionista ao fundo"
      />

      {/* Section 3: Recursos */}
      <RecursosSection />

      {/* Section 4: Outras Vantagens */}
      <VantagensSection />

      {/* Section 5: Para quem é */}
      <ParaQuemSection />

      {/* Section 6: Cardápio Digital */}
      {/*<CardapioDigitalSection />*/}

      {/* Section 7: Por que contratar */}
      <PorQueContratarSection />

      {/* Prova Social: Mural de depoimentos (texto + vídeo, unificados) */}
      <WallOfLoveSection
        depoimentos={depoimentos}
        videos={videosData}
        badge="Depoimentos"
        title="Um mural de resultados reais"
        subtitle="Cada card aqui é um hoteleiro de verdade, contando como a Foco elevou o padrão de serviço."
      />

      {/* Prova Social: Na Mídia */}
      <NaMidiaSection artigos={artigosMidia} />

      {/* Bloco de Autoridade e Confiança */}
      <NumerosSection numeros={numeros} />
      <TrustedLogosMarquee />

      {/* Prova Social: Carrossel de Produtos */}
      <SoftwareProductsCarousel />

      <UltimasDoBlogSection productSlug="aplicativo-de-hospedagem" />

      {/* Prova Social: FAQ */}
      <FAQAccordion
        items={[
          { id: "1", question: "O que é o Foco Pass?", answer: "O Foco Pass é um aplicativo de hospedagem que automatiza a jornada do hóspede, desde o check-in online até o pagamento via PIX, oferecendo também descontos exclusivos em parceiros do destino." },
          { id: "2", question: "Como funciona o check-in online?", answer: "O hóspede pode fazer o check-in antes mesmo de chegar ao hotel, diretamente pelo aplicativo. Isso elimina filas na recepção e permite que ele vá direto para o quarto." },
          { id: "3", question: "Quais são os descontos disponíveis?", answer: "O Foco Pass oferece descontos exclusivos em restaurantes, passeios turísticos, transfers, ingressos para atrações e espetáculos nos principais destinos do Brasil." },
          { id: "4", question: "O cardápio digital funciona para room service?", answer: "Sim! O hóspede pode acessar o cardápio completo do restaurante e do room service pelo aplicativo, fazer pedidos e receber no conforto do quarto." },
          { id: "5", question: "O Foco Pass é seguro?", answer: "Sim, o sistema possui total segurança para garantir que apenas o hóspede registrado tenha acesso ao aplicativo durante sua estadia." },
          { id: "6", question: "Qual o melhor aplicativo de hospedagem para hotéis e pousadas?", answer: "O melhor aplicativo de hospedagem é o que une check-in digital, comunicação por WhatsApp, cardápio e concierge virtual num só lugar — exatamente o que o Foco Pass entrega, já integrado ao PMS e ao motor de reservas do seu hotel ou pousada." },
        ]}
        title="Dúvidas Frequentes"
        subtitle="Tire suas dúvidas sobre o Foco Pass"
        badge="FAQ"
        showContactButton
      />

      {/* Lead Capture CTA */}
      <LeadCaptureCTA
        title="Pronto para transformar a experiência dos seus hóspedes?"
        subtitle="Solicite uma demonstração e descubra como o Foco Pass pode elevar o padrão de serviço do seu hotel."
        badge="Comece agora"
        source="aplicativo-de-hospedagem"
        leadTitle={LEAD_TITLE}
      />
    </div>
  );
}

export { ExperienciaHospedePage };
